<template>
  <header class="dbt">
    <!-- Breadcrumbs -->
    <div class="dbt-bread">
      <span class="dbt-seg">Workspace</span>
      <span class="dbt-sep">/</span>
      <span class="dbt-seg active">{{ pageName }}</span>
    </div>

    <!-- Search -->
    <div class="dbt-search-wrap" v-click-outside="closeSearch">
      <div class="dbt-search" :class="{ 'dbt-search--open': open }">
        <svg class="dbt-search-ico" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        <input
          ref="input"
          v-model="query"
          type="text"
          placeholder="Pesquisar..."
          class="dbt-input"
          @focus="open = true"
          @input="onInput"
          @keydown.down.prevent="moveDown"
          @keydown.up.prevent="moveUp"
          @keydown.enter.prevent="selectFocused"
          @keydown.esc="closeSearch"
        />
        <kbd class="dbt-kbd">⌘ K</kbd>
      </div>

      <!-- Dropdown -->
      <transition name="dbt-drop">
        <div v-if="open" class="dbt-dropdown">
          <!-- Category sidebar -->
          <div class="dbt-cats">
            <button
              v-for="cat in categories"
              :key="cat.key"
              class="dbt-cat"
              :class="{ 'dbt-cat--active': activeCategory === cat.key }"
              @mousedown.prevent="setCategory(cat.key)"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
                <path :d="cat.icon" />
              </svg>
              <span>{{ cat.label }}</span>
            </button>
          </div>

          <!-- Results panel -->
          <div class="dbt-results">
            <!-- Empty state -->
            <div v-if="!query" class="dbt-empty">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round" class="dbt-empty-ico">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <p class="dbt-empty-title">Digite para pesquisar em todos os módulos</p>
              <p class="dbt-empty-sub">Leads, VSLs, Fluxos...</p>
            </div>

            <!-- Loading -->
            <div v-else-if="loading" class="dbt-empty">
              <div class="dbt-spinner"></div>
              <p class="dbt-empty-sub">Buscando...</p>
            </div>

            <!-- No results -->
            <div v-else-if="results.length === 0" class="dbt-empty">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.3" class="dbt-empty-ico">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <p class="dbt-empty-title">Nenhum resultado encontrado</p>
              <p class="dbt-empty-sub">Tente outras palavras-chave</p>
            </div>

            <!-- Results list -->
            <template v-else>
              <div
                v-for="(item, i) in results"
                :key="item.type + item.id"
                class="dbt-result-item"
                :class="{ 'dbt-result-item--focused': focusedIndex === i }"
                @mousedown.prevent="navigate(item)"
              >
                <div class="dbt-result-icon" :class="'dbt-result-icon--' + item.type">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
                    <path :d="typeIcon(item.type)" />
                  </svg>
                </div>
                <div class="dbt-result-text">
                  <span class="dbt-result-title" v-html="highlight(item.title)"></span>
                  <span v-if="item.subtitle" class="dbt-result-sub">{{ item.subtitle }}</span>
                </div>
                <span class="dbt-result-badge" :class="'dbt-result-badge--' + item.type">{{ typeLabel(item.type) }}</span>
              </div>
            </template>
          </div>
        </div>
      </transition>
    </div>

    <!-- Actions -->
    <div class="dbt-actions">
      <div class="dbt-live-pill">
        <span class="dbt-live-dot"></span>
        <span class="dbt-live-num mono">{{ viewers }}</span>
        <span class="dbt-live-label">viewers</span>
      </div>
      <button class="dbt-btn" title="Notificações">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">
          <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0"/>
        </svg>
        <span class="dbt-notif-dot"></span>
      </button>
    </div>
  </header>
</template>

<script>
import axios from 'axios'

const API = process.env.VUE_APP_HOST_API_BACKEND || '/api/dkbackend'

const CATEGORIES = [
  { key: 'all',    label: 'Todas as categorias', icon: 'M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z' },
  { key: 'leads',  label: 'Leads',               icon: 'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8z' },
  { key: 'vsls',   label: 'VSLs',                icon: 'M2 7a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V7zm18-1l4 3v6l-4 3V6z' },
  { key: 'fluxos', label: 'Fluxos',              icon: 'M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z' },
]

const TYPE_ICONS = {
  lead:  'M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8z',
  vsl:   'M2 7a2 2 0 012-2h12a2 2 0 012 2v10a2 2 0 01-2 2H4a2 2 0 01-2-2V7zm18-1l4 3v6l-4 3V6z',
  fluxo: 'M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z',
}
const TYPE_LABELS = { lead: 'Lead', vsl: 'VSL', fluxo: 'Fluxo' }

export default {
  name: 'DashTopbar',
  directives: {
    clickOutside: {
      bind(el, binding) {
        el.__co__ = e => { if (!el.contains(e.target)) binding.value() }
        document.addEventListener('mousedown', el.__co__)
      },
      unbind(el) { document.removeEventListener('mousedown', el.__co__) },
    },
  },
  props: {
    liveViewers: { type: Number, default: 0 },
    pageName: { type: String, default: 'Dashboard' },
  },
  data() {
    return {
      viewers: this.liveViewers,
      _timer: null,
      open: false,
      query: '',
      activeCategory: 'all',
      categories: CATEGORIES,
      results: [],
      loading: false,
      focusedIndex: -1,
      _debounce: null,
    }
  },
  computed: {
    userId() {
      try {
        const user = JSON.parse(localStorage.getItem('userData') || '{}')
        return user.id || null
      } catch (_) {
        return null
      }
    },
  },
  mounted() {
    this.fetchLiveViewers()
    this._timer = setInterval(() => this.fetchLiveViewers(), 10000)
    document.addEventListener('keydown', this.globalShortcut)
  },
  beforeDestroy() {
    clearInterval(this._timer)
    document.removeEventListener('keydown', this.globalShortcut)
  },
  methods: {
    async fetchLiveViewers() {
      if (!this.userId) { this.viewers = 0; return }
      try {
        const { data } = await axios.get(`${API}/live-viewers/${this.userId}`, {
          params: { window_sec: 90 },
        })
        this.viewers = Number(data?.active_viewers || 0)
      } catch (_) {
        // Se houver falha, mantém o último valor conhecido
      }
    },
    globalShortcut(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        this.open = true
        this.$nextTick(() => this.$refs.input && this.$refs.input.focus())
      }
    },
    closeSearch() {
      this.open = false
      this.focusedIndex = -1
    },
    setCategory(key) {
      this.activeCategory = key
      if (this.query) this.doSearch()
    },
    onInput() {
      this.open = true
      clearTimeout(this._debounce)
      this.focusedIndex = -1
      if (!this.query) { this.results = []; return }
      this._debounce = setTimeout(() => this.doSearch(), 280)
    },
    async doSearch() {
      this.loading = true
      try {
        const params = { q: this.query }
        if (this.activeCategory !== 'all') params.category = this.activeCategory
        const { data } = await axios.get(`${API}/search`, { params })
        this.results = data.results || []
      } catch (e) {
        console.error('[search]', e)
        this.results = []
      } finally {
        this.loading = false
      }
    },
    moveDown() {
      if (this.focusedIndex < this.results.length - 1) this.focusedIndex++
    },
    moveUp() {
      if (this.focusedIndex > 0) this.focusedIndex--
    },
    selectFocused() {
      if (this.focusedIndex >= 0 && this.results[this.focusedIndex]) {
        this.navigate(this.results[this.focusedIndex])
      }
    },
    navigate(item) {
      this.closeSearch()
      this.query = ''
      this.$router.push(item.route).catch(() => {})
    },
    typeIcon(type) { return TYPE_ICONS[type] || TYPE_ICONS.lead },
    typeLabel(type) { return TYPE_LABELS[type] || type },
    highlight(text) {
      if (!this.query || !text) return text
      const escaped = this.query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
      return String(text).replace(new RegExp(`(${escaped})`, 'gi'), '<mark>$1</mark>')
    },
  },
}
</script>

<style>
.dbt {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 20px;
  padding: 0 28px;
  height: 64px;
  background: rgba(10,15,26,.6);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid var(--wr-line);
  position: sticky;
  top: 0;
  z-index: 50;
  font-family: 'Inter', sans-serif;
}
.dbt-bread { display: flex; align-items: center; gap: 6px; white-space: nowrap; }
.dbt-seg { font-size: 13.5px; color: var(--wr-dim); font-weight: 500; }
.dbt-seg.active { color: var(--wr-text); font-weight: 600; }
.dbt-sep { color: var(--wr-dim); font-size: 13px; }

/* ── Search wrap ── */
.dbt-search-wrap { display: flex; justify-content: center; position: relative; }
.dbt-search {
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 520px;
  background: rgba(255,255,255,.04);
  border: 1px solid var(--wr-line-2);
  border-radius: 10px;
  padding: 0 12px;
  gap: 8px;
  transition: border-color .2s, box-shadow .2s;
}
.dbt-search:focus-within,
.dbt-search--open { border-color: rgba(232,106,16,.5); box-shadow: 0 0 0 3px rgba(232,106,16,.1); }
.dbt-search-ico { color: var(--wr-dim); flex-shrink: 0; }
.dbt-input { flex: 1; background: none; border: none; outline: none; color: var(--wr-text); font-size: 13.5px; font-family: 'Inter', sans-serif; padding: 10px 0; }
.dbt-input::placeholder { color: var(--wr-dim); }
.dbt-kbd { font-size: 11px; font-family: 'JetBrains Mono', monospace; color: var(--wr-dim); background: rgba(255,255,255,.06); padding: 2px 6px; border-radius: 5px; border: 1px solid var(--wr-line-2); white-space: nowrap; flex-shrink: 0; }

/* ── Dropdown ── */
.dbt-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  width: 620px;
  max-width: 96vw;
  background: #141b27;
  border: 1px solid rgba(255,255,255,.1);
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0,0,0,.6), 0 0 0 1px rgba(255,255,255,.04);
  display: flex;
  overflow: hidden;
  z-index: 9999;
  max-height: 480px;
}

/* ── Categories ── */
.dbt-cats {
  width: 190px;
  flex-shrink: 0;
  border-right: 1px solid rgba(255,255,255,.07);
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow-y: auto;
}
.dbt-cat {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  padding: 9px 10px;
  border: none;
  background: transparent;
  color: rgba(255,255,255,.55);
  font-size: 13px;
  font-weight: 500;
  font-family: 'Inter', sans-serif;
  border-radius: 9px;
  cursor: pointer;
  text-align: left;
  transition: background .12s, color .12s;
}
.dbt-cat:hover { background: rgba(255,255,255,.07); color: #e2e8f0; }
.dbt-cat--active { background: #3B82F6 !important; color: #fff !important; }
.dbt-cat--active svg { opacity: 1; }

/* ── Results panel ── */
.dbt-results {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

/* ── Empty state ── */
.dbt-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 8px;
  color: rgba(255,255,255,.3);
}
.dbt-empty-ico { opacity: .4; }
.dbt-empty-title { font-size: 13px; font-weight: 600; color: rgba(255,255,255,.5); margin: 0; text-align: center; }
.dbt-empty-sub { font-size: 12px; color: rgba(255,255,255,.3); margin: 0; text-align: center; }

/* ── Spinner ── */
.dbt-spinner {
  width: 24px; height: 24px; border-radius: 50%;
  border: 2px solid rgba(255,255,255,.1);
  border-top-color: #E86A10;
  animation: dbt-spin .7s linear infinite;
}
@keyframes dbt-spin { to { transform: rotate(360deg); } }

/* ── Result items ── */
.dbt-result-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 10px;
  border-radius: 9px;
  cursor: pointer;
  transition: background .12s;
}
.dbt-result-item:hover,
.dbt-result-item--focused { background: rgba(255,255,255,.07); }
.dbt-result-icon {
  width: 30px; height: 30px; border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.dbt-result-icon--lead   { background: rgba(59,130,246,.15); color: #3B82F6; }
.dbt-result-icon--vsl    { background: rgba(232,106,16,.15); color: #E86A10; }
.dbt-result-icon--fluxo  { background: rgba(16,185,129,.15); color: #10B981; }
.dbt-result-text { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.dbt-result-title { font-size: 13px; font-weight: 600; color: #e2e8f0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dbt-result-title mark { background: rgba(232,106,16,.3); color: #E86A10; border-radius: 2px; padding: 0 1px; }
.dbt-result-sub { font-size: 11.5px; color: rgba(255,255,255,.35); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.dbt-result-badge {
  font-size: 10px; font-weight: 700; padding: 2px 7px;
  border-radius: 5px; white-space: nowrap; flex-shrink: 0;
  text-transform: uppercase; letter-spacing: .04em;
}
.dbt-result-badge--lead   { background: rgba(59,130,246,.15); color: #3B82F6; }
.dbt-result-badge--vsl    { background: rgba(232,106,16,.15); color: #E86A10; }
.dbt-result-badge--fluxo  { background: rgba(16,185,129,.15); color: #10B981; }

/* ── Transition ── */
.dbt-drop-enter-active, .dbt-drop-leave-active { transition: opacity .15s, transform .15s; }
.dbt-drop-enter, .dbt-drop-leave-to { opacity: 0; transform: translateX(-50%) translateY(-6px); }

/* ── Actions ── */
.dbt-actions { display: flex; align-items: center; gap: 10px; }
.dbt-live-pill {
  display: flex; align-items: center; gap: 7px;
  padding: 6px 12px;
  background: rgba(40,199,111,.08); border: 1px solid rgba(40,199,111,.2);
  border-radius: 999px; white-space: nowrap;
}
.dbt-live-dot { width: 7px; height: 7px; background: #28C76F; border-radius: 50%; animation: wr-pulse 2s infinite; flex-shrink: 0; }
.dbt-live-num { font-family: 'JetBrains Mono', monospace; font-size: 13px; font-weight: 700; color: #28C76F; font-variant-numeric: tabular-nums; }
.dbt-live-label { font-size: 12px; color: rgba(40,199,111,.7); font-weight: 500; }
.dbt-btn {
  position: relative; width: 38px; height: 38px; border-radius: 10px;
  border: 1px solid var(--wr-line-2); background: rgba(255,255,255,.03);
  color: var(--wr-mute); cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  transition: background .15s, color .15s;
}
.dbt-btn:hover { background: rgba(255,255,255,.07); color: var(--wr-text); }
.dbt-notif-dot {
  position: absolute; top: 7px; right: 7px;
  width: 8px; height: 8px;
  background: #E86A10; border-radius: 50%;
  border: 1.5px solid var(--wr-ink-2);
}
</style>
